import React, { useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react'
import { createPortal } from 'react-dom'
import { UNSAFE_DataRouterContext, useBlocker } from 'react-router-dom'
import styles from './layers.module.css'

// The overlay layer stack (h 425-458). Every slide-out, dialog, popover and
// open list registers here while it is open, in the order it opened, and one
// Escape handler serves them all: a press closes the topmost layer only, so
// Escape over a history popover closes the popover and leaves the panel under
// it open; a second press closes the panel. The listener is on window in the
// capture phase and stops the press there, so no panel's own handler and no
// field under it sees an Escape the stack has already spent.
//
// A layer's close is its guarded close: when the layer says it holds unsaved
// changes, closing it (Escape, the scrim, the ×, Cancel, or navigating away)
// first asks "You have unsaved changes. Discard them?"; a clean layer closes
// at once, with no prompt. Focus goes back to whatever opened the layer when
// it closes, and a form panel's first field takes focus when it opens.
//
// A layer that dims the page and takes the pointer (a dialog, or a drawer with
// a scrim) says so with `modal`, and then holds to the dialog contract too:
// it is announced as a modal (aria-modal), focus moves into it when it opens,
// Tab and Shift+Tab stay inside it, and what is behind it cannot be reached
// from the keyboard or by a screen reader while it is open (the rest of the
// page is made inert). A popover or menu that leaves the page usable is not
// modal and says nothing of the kind.

type Layer = {
  close: () => void
  dirty: () => boolean
  // When it opened, in render order. Effects run child first, so a panel that
  // opens with a form already inside it registers after the form; the stack is
  // kept in this order instead, which puts the form on top where it belongs.
  seq: number
  // The layer's own element, when it gave one; a modal layer must.
  el?: RefObject<HTMLElement | null>
  modal: boolean
}

const stack: Layer[] = []
let opened = 0

function onKey(e: KeyboardEvent) {
  if (e.key === 'Tab' && !e.isComposing) return trapTab(e)
  if (e.key !== 'Escape' || e.isComposing || stack.length === 0) return
  e.stopImmediatePropagation()
  e.preventDefault()
  stack[stack.length - 1].close()
}

// The element that had focus before the one that has it now, so a layer whose
// first field took focus while it mounted still knows what opened it.
let lastFocus: Element | null = null
let prevFocus: Element | null = null
if (typeof document !== 'undefined') {
  document.addEventListener(
    'focusin',
    (e) => {
      if (e.target === lastFocus) return
      prevFocus = lastFocus
      lastFocus = e.target as Element
    },
    true
  )
}

// A press that starts inside an open layer and is released outside it (dragging
// to select text in a field, say) is not a click on the scrim. The browser
// still fires that click on the nearest common ancestor, which is the scrim, so
// it is stopped here, before any scrim's click-to-close sees it.
let pressedIn: Node | null = null
if (typeof document !== 'undefined') {
  document.addEventListener('mousedown', (e) => (pressedIn = e.target as Node), true)
  document.addEventListener(
    'click',
    (e) => {
      const down = pressedIn
      pressedIn = null
      const target = e.target as Node
      if (!down || down === target || !(target instanceof Node)) return
      const from = stack.find((l) => l.el?.current?.contains(down))?.el?.current
      if (from && !from.contains(target)) {
        e.stopPropagation()
        e.preventDefault()
      }
    },
    true
  )
}

const TABBABLE = 'a[href], button, input:not([type=hidden]), select, textarea, summary, [contenteditable=""], [contenteditable="true"], [tabindex]'

// What Tab can reach inside `root`: enabled, not tabindex=-1, not hidden.
export function tabbablesIn(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(TABBABLE)).filter((n) => {
    if ((n as HTMLButtonElement).disabled || n.getAttribute('tabindex') === '-1') return false
    if (n.closest('[hidden], [inert]')) return false
    const cs = typeof getComputedStyle === 'function' ? getComputedStyle(n) : null
    return !(cs && (cs.display === 'none' || cs.visibility === 'hidden'))
  })
}

// The topmost modal layer that has an element to hold focus in.
function topModal(): { layer: Layer; root: HTMLElement; index: number } | null {
  for (let i = stack.length - 1; i >= 0; i--) {
    const root = stack[i].el?.current
    if (stack[i].modal && root) return { layer: stack[i], root, index: i }
  }
  return null
}

// Tab and Shift+Tab stay inside the topmost modal layer: past its last
// control the first one is next, and before its first the last. A popover that
// opened over the modal, with its own element outside the modal's, is let be.
function trapTab(e: KeyboardEvent) {
  const top = topModal()
  if (!top) return
  const active = document.activeElement as HTMLElement | null
  for (let i = top.index + 1; i < stack.length; i++) {
    const r = stack[i].el?.current
    if (r && !top.root.contains(r) && active && r.contains(active)) return
  }
  const tabbables = tabbablesIn(top.root)
  if (!tabbables.length) {
    e.preventDefault()
    top.root.focus()
    return
  }
  const first = tabbables[0]
  const last = tabbables[tabbables.length - 1]
  if (!active || !top.root.contains(active) || active === top.root) {
    e.preventDefault()
    ;(e.shiftKey ? last : first).focus()
  } else if (e.shiftKey && active === first) {
    e.preventDefault()
    last.focus()
  } else if (!e.shiftKey && active === last) {
    e.preventDefault()
    first.focus()
  }
}

// Everything behind the topmost modal layer is made inert, so the keyboard and
// assistive technology cannot reach it. The modal's own element, anything on
// the way to it, and the elements of layers opened over it stay live; so do
// empty elements with nothing to focus (a scrim has nothing to reach, and must keep its click) and
// live regions. Layers over the modal that gave no element are taken to be
// inside it. Only what this marked is ever cleared.
const inerted = new Set<Element>()

function syncModality() {
  for (const n of inerted) n.removeAttribute('inert')
  inerted.clear()
  if (typeof document === 'undefined') return
  watchBehind()
  const top = topModal()
  if (!top) return
  const keep: HTMLElement[] = [top.root]
  for (let i = top.index + 1; i < stack.length; i++) {
    const r = stack[i].el?.current
    if (r) keep.push(r)
  }
  const visit = (parent: Element) => {
    for (const child of Array.from(parent.children)) {
      if (keep.some((k) => child === k)) continue
      if (keep.some((k) => child.contains(k))) {
        visit(child)
        continue
      }
      if (child.matches('script, style, link, [aria-live], [role=status], [role=alert]')) continue
      if (!child.children.length && !child.matches(TABBABLE)) continue
      if (!child.hasAttribute('inert')) {
        child.setAttribute('inert', '')
        inerted.add(child)
      }
    }
  }
  visit(document.body)
}

// Something that arrives behind an open modal (a banner or notice that loads late) is out of reach too: while a modal is
// open the page is watched for what is added to it, and looked over again. Only additions matter; the attribute this sets
// is not watched, so it cannot set itself off.
let behind: MutationObserver | null = null
function watchBehind() {
  const want = typeof MutationObserver !== 'undefined' && typeof document !== 'undefined' && !!topModal()
  if (want && !behind) {
    behind = new MutationObserver(syncModality)
    behind.observe(document.body, { childList: true, subtree: true })
  } else if (!want && behind) {
    behind.disconnect()
    behind = null
  }
}

function push(layer: Layer) {
  if (stack.length === 0) window.addEventListener('keydown', onKey, true)
  let i = stack.length
  while (i > 0 && stack[i - 1].seq > layer.seq) i--
  stack.splice(i, 0, layer)
  syncModality()
}

// Whether closing this layer would take unsaved changes with it: its own, or
// those of a layer open over it, since closing a panel closes what is on it.
function holdsChanges(layer: Layer | null, own: boolean) {
  if (own) return true
  const i = layer ? stack.indexOf(layer) : -1
  return i >= 0 && stack.slice(i + 1).some((l) => l.dirty())
}

function pull(layer: Layer) {
  const i = stack.indexOf(layer)
  if (i >= 0) stack.splice(i, 1)
  if (stack.length === 0) window.removeEventListener('keydown', onKey, true)
  syncModality()
}

// For tests, and for the navigation guard.
export function openLayerCount() {
  return stack.length
}
export function anyDirtyLayer() {
  return released === 0 && stack.some((l) => l.dirty())
}

// Non-zero while a Discard is being carried out.
let released = 0

const FIELD = 'input:not([type=hidden]):not([disabled]), select:not([disabled]), textarea:not([disabled])'

export type LayerOptions = {
  // The layer holds changes nobody has saved: closing it asks first.
  dirty?: boolean
  // The layer's own element, which a form panel's first field is found in.
  el?: RefObject<HTMLElement | null>
  // A form panel: its first field takes focus when it opens, or, for a panel
  // that loads before it has fields, when this turns true.
  focusFirst?: boolean
  // A dialog, or a drawer behind a scrim: see the note at the top. Needs `el`.
  modal?: boolean
  // A write from this layer is out (h 3978-3982): the panel stays open, so no
  // way out closes it until the write has answered.
  busy?: boolean
}

// Registers a layer while `open`. `close` is what actually closes it; the
// returned `requestClose` is the guarded close every way out should call, and
// `confirm` is the discard question, for the layer to render (it portals over
// everything, and is a layer of its own).
export function useLayer(open: boolean, close: () => void, opts: LayerOptions = {}) {
  // The action the discard question is holding, while it is asked.
  const [asking, setAsking] = useState<null | (() => void)>(null)
  const live = useRef({ close, dirty: !!opts.dirty, busy: !!opts.busy })
  live.current = { close, dirty: !!opts.dirty, busy: !!opts.busy }
  const current = useRef<Layer | null>(null)
  // Numbered as it opens, during render (parent before child).
  const seq = useRef(0)
  const wasOpen = useRef(false)
  if (open && !wasOpen.current) seq.current = ++opened
  wasOpen.current = open

  // `guard(then)` runs `then` at once when nothing is unsaved, and after a
  // Discard when something is: for a way out of the layer that is not a close
  // (a link from the panel to another screen).
  const guard = useRef((then: () => void) => {
    if (live.current.busy) return
    if (holdsChanges(current.current, live.current.dirty)) setAsking(() => then)
    else then()
  }).current
  const requestClose = useRef(() => guard(() => live.current.close())).current

  // What opened the layer is decided once, as it opens. This effect can run again for the same opening (the dev build runs
  // every effect twice), and by then focus has moved into the layer, which would make the layer's own first control, or
  // the element focus passed through on the way, look like what opened it.
  const openedBy = useRef<{ seq: number; el: HTMLElement | null } | null>(null)

  useLayoutEffect(() => {
    if (!open) return
    if (openedBy.current?.seq !== seq.current) {
      const a = document.activeElement
      const inside = !!(a && opts.el?.current?.contains(a))
      openedBy.current = { seq: seq.current, el: (inside ? prevFocus : a && a !== document.body ? a : null) as HTMLElement | null }
    }
    const opener = openedBy.current.el
    const layer: Layer = { close: requestClose, dirty: () => live.current.dirty, seq: seq.current, el: opts.el, modal: !!opts.modal && !!opts.el }
    current.current = layer
    push(layer)
    const root = layer.modal ? opts.el?.current : null
    const marked = !!root && !root.hasAttribute('aria-modal')
    if (root && marked) root.setAttribute('aria-modal', 'true')
    return () => {
      pull(layer)
      if (root && marked) root.removeAttribute('aria-modal')
      setAsking(null)
      // Back to the opener once the layer has gone, unless it was only
      // remounted (StrictMode), focus has already gone somewhere on purpose
      // (a press on another row), or the opener went with the screen.
      queueMicrotask(() => {
        if (current.current !== layer) return
        const now = document.activeElement
        const lost = !now || now === document.body || !now.isConnected
        if (lost && opener?.isConnected && typeof opener.focus === 'function') opener.focus()
      })
    }
  }, [open])

  useEffect(() => {
    if (!open || !(opts.focusFirst || opts.modal)) return
    const root = opts.el?.current
    if (!root) return
    // A modal that has nothing to put focus on yet (a form still loading, or no field at all) takes it
    // itself, so the keyboard is in the dialog and the dialog's name is read; a later first field replaces that.
    const placed = root.contains(document.activeElement) && document.activeElement !== root
    if (placed) return
    const field = opts.focusFirst ? root.querySelector<HTMLElement>(FIELD) : null
    if (field) field.focus()
    else if (opts.modal && !root.contains(document.activeElement)) {
      if (!root.hasAttribute('tabindex')) root.setAttribute('tabindex', '-1')
      root.focus()
    }
  }, [open, !!opts.focusFirst])

  const confirm = asking ? (
    <DiscardDialog
      onKeep={() => setAsking(null)}
      onDiscard={() => {
        setAsking(null)
        // Already answered: a navigation the action makes is not asked again.
        released++
        try {
          asking()
        } finally {
          released--
        }
      }}
    />
  ) : null

  return { requestClose, guard, confirm }
}

// Whether a form's values have moved from what they were when it opened, for
// a form with no change tracking of its own. `ready` is false while the form
// is still loading what it starts from; the first ready values are the start.
export function useChanged(value: unknown, ready = true) {
  const json = JSON.stringify(value)
  const start = useRef<string | null>(null)
  if (ready && start.current === null) start.current = json
  return start.current !== null && start.current !== json
}

// The one discard question (h 448-453). Keep editing is the safe answer, so it
// takes focus, and it is what Escape and the scrim mean. Events stop at the
// dialog: it is portalled, but React would still bubble them to the panel
// that rendered it, whose scrim would ask again.
// The same question box asks other two-way questions (Sign out); the safe
// answer is always the first button and takes focus.
export function DiscardDialog({
  onKeep,
  onDiscard,
  question = 'You have unsaved changes. Discard them?',
  keepLabel = 'Keep editing',
  discardLabel = 'Discard',
}: {
  onKeep: () => void
  onDiscard: () => void
  question?: string
  keepLabel?: string
  discardLabel?: string
}) {
  const box = useRef<HTMLDivElement>(null)
  useLayer(true, onKeep, { el: box, modal: true })
  const keep = useRef<HTMLButtonElement>(null)
  useEffect(() => {
    keep.current?.focus()
  }, [])
  const stop = (e: React.SyntheticEvent) => e.stopPropagation()
  return createPortal(
    <div
      className={styles.scrim}
      onMouseDown={stop}
      onKeyDown={stop}
      onClick={(e) => {
        e.stopPropagation()
        if (e.target === e.currentTarget) onKeep()
      }}
    >
      <div ref={box} className={styles.box} role="alertdialog" aria-modal="true" aria-labelledby="dp-discard-q">
        <p id="dp-discard-q" className={styles.q}>
          {question}
        </p>
        <div className={styles.actions}>
          <button ref={keep} type="button" className={styles.keep} onClick={onKeep}>
            {keepLabel}
          </button>
          <button type="button" className={styles.discard} onClick={onDiscard}>
            {discardLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

// Navigating away while a layer holds unsaved changes asks the same question
// (h 454-456): a rail link, a navigate() from code and the browser's back
// button all go through the router, and the router holds them here. Only a
// data router can hold a navigation; under a plain router (the page tests)
// there is nothing to hold, and this renders nothing.
export function NavigationGuard() {
  const dataRouter = React.useContext(UNSAFE_DataRouterContext)
  return dataRouter ? <Blocker /> : null
}

function Blocker() {
  const blocker = useBlocker(({ currentLocation, nextLocation }) => currentLocation.pathname !== nextLocation.pathname && anyDirtyLayer())
  if (blocker.state !== 'blocked') return null
  return <DiscardDialog onKeep={() => blocker.reset?.()} onDiscard={() => blocker.proceed?.()} />
}
